import axios, { type AxiosInstance } from 'axios'
import { emitUnauthorized } from '../store/alerts'

function extractResource(url?: string): string {
  if (!url) return 'this resource'
  const path = url.split('?')[0]
  const segments = path.split('/').filter(Boolean)
  const apiIdx = segments.indexOf('api')
  const after = apiIdx >= 0 ? segments.slice(apiIdx + 1) : segments
  return after[0] ?? 'this resource'
}

const AUTH_URL = import.meta.env.VITE_AUTH_URL ?? 'http://localhost:6000'
const BUZZ_URL = import.meta.env.VITE_BUZZ_URL ?? 'http://localhost:3000'
const MAIL_URL = import.meta.env.VITE_MAIL_URL ?? 'http://localhost:5000'
const STORAGE_URL = import.meta.env.VITE_STORAGE_URL ?? 'http://localhost:7000'
const BRAINER_URL = import.meta.env.VITE_BRAINER_URL ?? 'http://localhost:3005'
const RAIDER_URL = import.meta.env.VITE_RAIDER_URL ?? 'http://localhost:3010'
const INVOICE_FACTEUR_URL = import.meta.env.VITE_INVOICE_FACTEUR_URL ?? 'http://localhost:3012'
const ORDER_PARSER_URL = import.meta.env.VITE_ORDER_PARSER_URL ?? 'http://localhost:3011'
const ENERGY_OS_URL = import.meta.env.VITE_ENERGY_OS_URL ?? 'http://localhost:3050'
const GARAGE_URL = import.meta.env.VITE_GARAGE_URL ?? 'http://localhost:3060'
export const LENS_URL = import.meta.env.VITE_LENS_URL ?? 'http://localhost:3030'
export const DOCS_URL = import.meta.env.VITE_DOCS_URL ?? 'http://localhost:3020'
// The single business EnergyOS homeowner signup joins (see Phase 6 plan) — not a general
// multi-tenant concept, so it's a plain env var like the service URLs above, not an API lookup.
export const ENERGY_BRAIN_BUSINESS_ID = import.meta.env.VITE_ENERGY_BRAIN_BUSINESS_ID ?? ''

// Shared across every axios instance below (module-level, not per-instance) — auth-service's
// refresh tokens are single-use/rotating (each call revokes the old one and issues a new one), so
// if two+ requests 401 around the same moment (routine — most pages fire several parallel calls),
// letting each independently call /api/auth/refresh means only the first redeems successfully;
// every other concurrent attempt sends the now-already-consumed token, gets rejected, and used to
// wipe localStorage + hard-redirect to /login of its own accord — logging the user out of a
// session that had just been validly refreshed a moment earlier. All concurrent 401/498s now await
// this one in-flight refresh instead of each starting their own.
let refreshPromise: Promise<string | null> | null = null

async function refreshAccessToken(): Promise<string | null> {
  if (refreshPromise) return refreshPromise
  refreshPromise = (async () => {
    try {
      const refreshToken = localStorage.getItem('refreshToken')
      if (!refreshToken) return null
      const { data } = await axios.post(`${AUTH_URL}/api/auth/refresh`, { refreshToken })
      localStorage.setItem('accessToken', data.accessToken)
      localStorage.setItem('refreshToken', data.refreshToken)
      // Refresh also returns a fresh `user` (roles/enabledModules may have changed since
      // login) — persist it so the next page load/render picks up the latest values.
      if (data.user) localStorage.setItem('user', JSON.stringify(data.user))
      return data.accessToken as string
    } catch {
      return null
    } finally {
      refreshPromise = null
    }
  })()
  return refreshPromise
}

function attachToken(instance: AxiosInstance) {
  instance.interceptors.request.use((config) => {
    const token = localStorage.getItem('accessToken')
    if (token) config.headers.Authorization = `Bearer ${token}`
    return config
  })

  instance.interceptors.response.use(
    (res) => res,
    async (error) => {
      const original = error.config
      const status = error.response?.status
      if (status === 403) {
        emitUnauthorized(extractResource(original?.url))
        return Promise.reject(error)
      }
      if ((status === 498 || status === 401) && !original._retry) {
        original._retry = true
        const accessToken = await refreshAccessToken()
        if (accessToken) {
          original.headers.Authorization = `Bearer ${accessToken}`
          return instance(original)
        }
        localStorage.clear()
        window.location.href = '/login'
      }
      return Promise.reject(error)
    }
  )
}

export const authClient = axios.create({ baseURL: `${AUTH_URL}/api` })
export const buzzClient = axios.create({ baseURL: `${BUZZ_URL}/api/buzz` })
export const mailClient = axios.create({ baseURL: `${MAIL_URL}/api/mail` })
export const storageClient = axios.create({ baseURL: `${STORAGE_URL}/api/storage`, headers: { 'x-component-id': 'console' } })
export const brainerClient = axios.create({ baseURL: `${BRAINER_URL}/api/brainer` })
export const raiderClient = axios.create({ baseURL: `${RAIDER_URL}/api/raider` })
export const lensClient = axios.create({ baseURL: `${LENS_URL}/api/lens` })
export const invoiceFacteurClient = axios.create({ baseURL: `${INVOICE_FACTEUR_URL}/api/invoice-facteur` })
export const orderParserClient = axios.create({ baseURL: `${ORDER_PARSER_URL}/api/orders` })
export const energyOsClient = axios.create({ baseURL: `${ENERGY_OS_URL}/api/energy-os` })
export const garageClient = axios.create({ baseURL: `${GARAGE_URL}/api/garage` })

;[authClient, buzzClient, mailClient, storageClient, brainerClient, raiderClient, lensClient, invoiceFacteurClient, orderParserClient, energyOsClient, garageClient].forEach(attachToken)

// Deliberately NOT run through attachToken — this hits Brainer's public, unauthenticated
// openplace-token routes (no JWT concept at all, just the opaque per-record token in the URL).
// Attaching a stale business-user Authorization header from localStorage, or wiring in the
// refresh/401-redirect-to-login interceptor, would be actively wrong on a page a homeowner with
// no Rabbit account is expected to open directly.
export const openPlaceTokenClient = axios.create({ baseURL: `${BRAINER_URL}/api/brainer` })
